import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { closeAssistant, toggleAssistant } from "portal/slices/assistantSlice";
import { executeCommand } from "portal/services/smartCommandEngine";
import AssistantInput from "./AssistantInput";
import AssistantResponseRenderer from "./AssistantResponseRenderer";
import { useBranding } from "context/BrandingProvider";

// ── sessionStorage helpers ──────────────────────────────────────────────────

const STORAGE_KEY = "vistaar_assistant_history";

const loadHistory = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveHistory = (history) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // storage full — silently fail
  }
};

// ── Time formatter ──────────────────────────────────────────────────────────

const formatTime = (ts) => {
  const d = new Date(ts);
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${m} ${ampm}`;
};

// ── Floating button ─────────────────────────────────────────────────────────

const FloatingButton = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.assistant.isOpen);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="fixed right-0 bottom-[calc(env(safe-area-inset-bottom,0px)+72px)] lg:bottom-6 z-[10] flex items-center">
      {/* Hover tooltip — only shown when closed and the button itself is hovered */}
      {!isOpen && (
        <span
          className={`mr-2 px-2.5 py-1.5 rounded-lg bg-content-primary text-white text-[11px] font-medium whitespace-nowrap shadow-elevation-2 pointer-events-none transition-all duration-200 ${
            isHovered ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"
          }`}
        >
          Ask Assistant <span className="opacity-60">⌘K</span>
        </span>
      )}

      {/* Docked half-circle handle — flush against the edge, chevron flips on open */}
      <button
        onClick={() => dispatch(toggleAssistant())}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`h-12 rounded-l-full shadow-elevation-2 flex items-center justify-start transition-all duration-200 ease-out ${
          isHovered ? "w-9 pl-2.5" : "w-7 pl-2"
        } ${
          isOpen
            ? "bg-content-primary hover:bg-content-primary/90"
            : "bg-brand-500 hover:bg-brand-600"
        }`}
        style={{
          boxShadow: isOpen
            ? "-2px 2px 12px rgba(0,0,0,0.15)"
            : "-2px 2px 12px rgba(122,110,254,0.3)",
        }}
        aria-label={isOpen ? "Close assistant" : "Open assistant (⌘K)"}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
    </div>
  );
};

// ── Conversation message ────────────────────────────────────────────────────

const UserMessage = ({ text, timestamp }) => (
  <div className="flex flex-col items-end mb-3">
    <div className="max-w-[85%] px-3 py-2 bg-brand-500 text-white text-xs rounded-2xl rounded-br-md leading-relaxed">
      {text}
    </div>
    {timestamp && (
      <span className="text-[9px] text-content-tertiary mt-0.5 mr-1">{formatTime(timestamp)}</span>
    )}
  </div>
);

const AssistantMessage = ({ response, timestamp, onSuggestionClick }) => (
  <div className="flex flex-col items-start mb-3">
    <div className="max-w-[95%]">
      <AssistantResponseRenderer response={response} onSuggestionClick={onSuggestionClick} />
    </div>
    {timestamp && (
      <span className="text-[9px] text-content-tertiary mt-0.5 ml-1">{formatTime(timestamp)}</span>
    )}
  </div>
);

const TypingIndicator = () => (
  <div className="flex justify-start mb-3">
    <div className="px-4 py-2.5 bg-surface-card border border-surface-border rounded-2xl rounded-bl-md shadow-elevation-1">
      <div className="flex items-center gap-1">
        <span className="w-1.5 h-1.5 rounded-badge bg-content-tertiary animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-1.5 h-1.5 rounded-badge bg-content-tertiary animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-1.5 h-1.5 rounded-badge bg-content-tertiary animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  </div>
);

// ── Main drawer ─────────────────────────────────────────────────────────────

const AIAssistantDrawer = () => {
  const { currency } = useBranding();
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.assistant.isOpen);
  const permissions = useSelector((state) => state.auth.permissions);
  const roleId = useSelector((state) => state.auth.user?.RoleId);
  const [history, setHistory] = useState(loadHistory);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  // Persist history to sessionStorage on change
  useEffect(() => {
    saveHistory(history);
  }, [history]);

  // Cmd+K / Ctrl+K global shortcut + ESC to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K (Mac) or Ctrl+K (Win/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        dispatch(toggleAssistant());
      }
      // ESC closes
      if (e.key === "Escape" && isOpen) {
        dispatch(closeAssistant());
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, dispatch]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, isLoading]);

  const handleSubmit = useCallback(async (query) => {
    // Add user message
    setHistory((prev) => [...prev, { role: "user", text: query, timestamp: Date.now() }]);
    setIsLoading(true);

    try {
      // RoleId 1/2 open every page, as PermissionRoute allows.
      const result = await executeCommand(query, {
        isAdmin: roleId === 1 || roleId === 2,
        permissions: permissions || [],
      });
      setHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          response: result,
          timestamp: Date.now(),
        },
      ]);
    } catch {
      setHistory((prev) => [
        ...prev,
        {
          role: "assistant",
          response: {
            type: "error",
            category: "general",
            title: "Error",
            message: "Something went wrong. Please try again.",
            confidence: 0,
          },
          timestamp: Date.now(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [roleId, permissions]);

  const handleClearHistory = useCallback(() => {
    setHistory([]);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <>
      <FloatingButton />

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[998] animate-fadeIn"
          style={{ backgroundColor: "rgba(0,0,0,0.2)", backdropFilter: "blur(2px)" }}
          onClick={() => dispatch(closeAssistant())}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full z-[999] bg-surface-card shadow-elevation-3 transition-transform duration-300 ease-in-out flex flex-col w-full sm:w-[420px] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="px-4 py-3.5 border-b border-surface-border flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-button bg-gradient-to-br from-brand-500 to-brand-400 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 2a7 7 0 017 7c0 3-2 5.5-4 7l-3 3.5L9 16c-2-1.5-4-4-4-7a7 7 0 017-7z" />
                  <circle cx="12" cy="9" r="1.5" fill="white" stroke="none" />
                </svg>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-content-primary">Vistaar Assistant</h2>
                <p className="text-[10px] text-content-tertiary">
                  Rule-based intelligent assistant
                  <span className="ml-1.5 px-1 py-px bg-surface-hover rounded text-[9px] font-medium text-content-secondary">⌘K</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {history.length > 0 && (
                <button
                  onClick={handleClearHistory}
                  className="w-7 h-7 flex items-center justify-center rounded-button hover:bg-surface-hover transition-colors"
                  title="Clear history"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="stroke-content-tertiary" strokeWidth="2" strokeLinecap="round">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 105.64-11.36L1 10" />
                  </svg>
                </button>
              )}
              <button
                onClick={() => dispatch(closeAssistant())}
                className="w-7 h-7 flex items-center justify-center rounded-button hover:bg-surface-hover transition-colors"
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1L13 13M1 13L13 1" className="stroke-content-tertiary" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Conversation area */}
        <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-4 bg-surface-bg">
          {history.length === 0 && !isLoading ? (
            <EmptyState onSuggestionClick={handleSubmit} />
          ) : (
            <>
              {history.map((entry, i) => (
                <React.Fragment key={i}>
                  {entry.role === "user" ? (
                    <UserMessage text={entry.text} timestamp={entry.timestamp} />
                  ) : (
                    <AssistantMessage
                      response={entry.response}
                      timestamp={entry.timestamp}
                      onSuggestionClick={handleSubmit}
                    />
                  )}
                </React.Fragment>
              ))}
              {isLoading && <TypingIndicator />}
            </>
          )}
        </div>

        {/* Input area */}
        <div className="px-4 py-3 border-t border-surface-border flex-shrink-0 bg-surface-card">
          <AssistantInput onSubmit={handleSubmit} isLoading={isLoading} />
        </div>

        {/* Footer */}
        <div className="px-4 py-1.5 border-t border-surface-border flex-shrink-0">
          <p className="text-[10px] text-content-tertiary text-center">
            No external AI — Rule-based intelligence engine
          </p>
        </div>
      </div>
    </>
  );
};

// ── Empty state ─────────────────────────────────────────────────────────────

const EmptyState = ({ onSuggestionClick }) => {
  const { currency } = useBranding();
  return (
  <div className="flex flex-col items-center justify-center h-full text-center px-4">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500/10 to-brand-400/10 flex items-center justify-center mb-4">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="stroke-brand-500" strokeWidth="1.5" strokeLinecap="round">
        <path d="M12 2a7 7 0 017 7c0 3-2 5.5-4 7l-3 3.5L9 16c-2-1.5-4-4-4-7a7 7 0 017-7z" />
        <circle cx="12" cy="9" r="1.5" className="fill-brand-500" stroke="none" />
        <circle cx="8.5" cy="9" r="1" className="fill-brand-500" stroke="none" />
        <circle cx="15.5" cy="9" r="1" className="fill-brand-500" stroke="none" />
      </svg>
    </div>
    <h3 className="text-sm font-semibold text-content-primary mb-1">How can I help?</h3>
    <p className="text-xs text-content-tertiary mb-5 max-w-[240px]">
      Ask about revenue, patients, appointments, or generate reports — all powered by local intelligence.
    </p>

    <div className="w-full space-y-1.5">
      {[
        { icon: "📊", text: "Generate monthly report", query: "generate monthly report" },
        { icon: "💰", text: "Revenue trend this month", query: "revenue trend this month" },
        { icon: "👥", text: "Show inactive patients", query: "show inactive patients" },
        { icon: "📋", text: `Pending above ${currency}5,000`, query: "show patients with pending above 5000" },
      ].map((item, i) => (
        <button
          key={i}
          onClick={() => onSuggestionClick(item.query)}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left rounded-card border border-surface-border hover:border-brand-500/30 hover:bg-brand-500/5 transition-all group"
        >
          <span className="text-sm">{item.icon}</span>
          <span className="text-xs text-content-secondary group-hover:text-brand-500 transition-colors">{item.text}</span>
        </button>
      ))}
    </div>
  </div>
  );
};

export default AIAssistantDrawer;
